import React from "react";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { ModalTypes } from "../../types/views";
import { Trans, useTranslation } from "@coworker/locales";
import { FullWidthButton } from "../../../SkapaButton";
import { InputPopupContainer } from "../../styles/styles";
import { MediaResponse, RoomMedia } from "../../types/media";
import { getTranslationKeyForRoomType } from "../../tools";
import { MediaTypes, RoomTypes } from "../../constants";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { useCreateMedia } from "../../hooks/useCreateMedia";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import styled from "styled-components";

const Instruction = styled.b`
  margin-left: 24px;
`;

interface AddRoomModalProps {
  modalVisibility: boolean;
  media: MediaResponse;
  onTriggerNewModal: (modalType: ModalTypes) => void;
}

export function AddRoomModal({
  modalVisibility,
  media,
  onTriggerNewModal,
}: AddRoomModalProps) {
  const { t } = useTranslation();

  const createMediaMutation = useCreateMedia();

  const [newRoomTypes, setNewRoomTypes] = React.useState<RoomTypes[]>([]);

  const handleAddRooms = async () => {
    if (newRoomTypes.length === 0) {
      return;
    }

    const newRooms = newRoomTypes.map(
      (roomType) =>
        ({
          roomType: roomType as RoomTypes,
          mediaType: MediaTypes.ROOM,
          storeId: media.media.storeId,
          active: media.media.active,
          name: t(getTranslationKeyForRoomType(roomType)),
          parentMediaId: media.media.id,
        }) as RoomMedia
    );

    // Create new rooms
    newRooms.forEach(async (room) => {
      await createMediaMutation.mutateAsync(room);
    });

    onTriggerNewModal(ModalTypes.DEFAULT_VIEW);
    setNewRoomTypes([]);
  };

  return (
    <FixaModal
      visible={modalVisibility}
      handleCloseBtn={() => {
        onTriggerNewModal(ModalTypes.DEFAULT_VIEW);
      }}
    >
      <FixaSheets
        labelledById="media-add-room-modal-header"
        size={"small"}
        header={
          <FixaModalHeader
            id="media-add-room-modal-header"
            ariaCloseTxt={t("closeActionString")}
            title={t("addRoomsString")}
          />
        }
        footer={
          <FixaModalFooter>
            <FullWidthButton
              disabled={false}
              text={t("continue2String")}
              type={"primary"}
              onClick={async () => {
                await handleAddRooms();
              }}
              data
            />
          </FixaModalFooter>
        }
      >
        <InputPopupContainer>
          <Instruction>
            <Trans>{"selectRoomsToAddString"}</Trans>
          </Instruction>
          <FixaListView id={"add-room-modal"} size="small" showDivider={true}>
            {Object.keys(RoomTypes)
              .filter((roomType) => roomType !== RoomTypes.NONE)
              .map((roomType) => (
                <FixaListViewItem
                  key={roomType}
                  id={roomType}
                  inset
                  control="checkbox"
                  onChange={() => {
                    const newRoomType = roomType as RoomTypes;
                    setNewRoomTypes((newRoomTypes) =>
                      newRoomTypes.includes(newRoomType)
                        ? newRoomTypes.filter((type) => type !== newRoomType)
                        : [...newRoomTypes, newRoomType]
                    );
                  }}
                  title={t(getTranslationKeyForRoomType(roomType as RoomTypes))}
                  controlProps={{
                    checked:
                      media.children.some(
                        (c) => (c as RoomMedia).roomType === roomType
                      ) || newRoomTypes.includes(roomType as RoomTypes),
                    value: roomType,
                  }}
                  disabled={media.children.some(
                    (c) => (c as RoomMedia).roomType === roomType
                  )}
                />
              ))}
          </FixaListView>
        </InputPopupContainer>
      </FixaSheets>
    </FixaModal>
  );
}
